/**
 * Prompt 命令 - 管理和使用 prompt 模板
 */

import { Command } from 'commander';
import { logger } from '../utils/logger';
import { withErrorHandling } from '../utils/error-handler';
import { PromptTemplateEngine } from '../core/prompt-template-engine';
import { colors } from '../utils/colors';
import { Table } from 'console-table-printer';
import fs from 'fs-extra';
import path from 'path';
import inquirer from 'inquirer';

export function createPromptCommand(): Command {
  const command = new Command('prompt');
  
  command
    .description('Manage and use prompt templates')
    .addCommand(createPromptListCommand())
    .addCommand(createPromptShowCommand())
    .addCommand(createPromptRenderCommand())
    .addCommand(createPromptValidateCommand())
    .addCommand(createPromptExportCommand());
  
  return command;
}

/**
 * prompt list - 列出所有 prompt 模板
 */
function createPromptListCommand(): Command {
  const command = new Command('list');
  
  command
    .description('List all prompt templates')
    .alias('ls')
    .option('-c, --category <category>', 'Filter by category')
    .option('-t, --tag <tag>', 'Filter by tag')
    .action(withErrorHandling(async (options) => {
      const engine = new PromptTemplateEngine();
      await engine.initialize();
      
      let templates = engine.getTemplates();
      
      // 应用过滤器
      if (options.category) {
        templates = templates.filter(t => t.metadata?.category === options.category);
      }
      
      if (options.tag) {
        templates = templates.filter(t => t.metadata?.tags?.includes(options.tag));
      }
      
      if (templates.length === 0) {
        console.log(colors.warning('No templates found matching the criteria.'));
        return;
      }
      
      console.log(colors.header('\n📋 Prompt Templates\n'));
      
      const table = new Table({
        columns: [
          { name: 'id', title: 'ID', alignment: 'left' },
          { name: 'name', title: 'Name', alignment: 'left' },
          { name: 'category', title: 'Category', alignment: 'center', colored: true },
          { name: 'vars', title: 'Variables', alignment: 'center' },
          { name: 'effectiveness', title: 'Effect', alignment: 'center' }
        ]
      });
      
      for (const template of templates) {
        table.addRow({
          id: template.id,
          name: template.name,
          category: (template.metadata?.category || 'general').toUpperCase(),
          vars: template.variables.filter(v => v.required).length + '/' + template.variables.length,
          effectiveness: template.metadata?.effectiveness ? `${template.metadata.effectiveness}%` : '-'
        });
      }
      
      table.printTable();
      
      // 显示类别统计
      const categories = new Map<string, number>();
      templates.forEach(t => {
        const cat = t.metadata?.category || 'general';
        categories.set(cat, (categories.get(cat) || 0) + 1);
      });
      
      console.log(colors.dim('\n📊 Categories:'));
      Array.from(categories.entries()).forEach(([cat, count]) => {
        console.log(colors.dim(`• ${cat}: ${count} templates`));
      });
    }));
  
  return command;
}

/**
 * prompt show - 显示模板详情
 */
function createPromptShowCommand(): Command {
  const command = new Command('show');
  
  command
    .description('Show template details')
    .argument('<template>', 'Template ID')
    .option('--example', 'Show example usage')
    .action(withErrorHandling(async (templateId: string, options) => {
      const engine = new PromptTemplateEngine();
      await engine.initialize();
      
      const template = engine.getTemplate(templateId);
      if (!template) {
        console.log(colors.error(`Template not found: ${templateId}`));
        return;
      }
      
      console.log(colors.header(`\n📋 Template: ${template.name}\n`));
      console.log(`ID: ${template.id}`);
      console.log(`Version: ${template.version}`);
      console.log(`Description: ${template.description}`);
      
      // 显示元数据
      if (template.metadata) {
        console.log(colors.info('\nMetadata:'));
        if (template.metadata.category) {
          console.log(`• Category: ${template.metadata.category}`);
        }
        if (template.metadata.tags) {
          console.log(`• Tags: ${template.metadata.tags.join(', ')}`);
        }
        if (template.metadata.complexity) {
          console.log(`• Complexity: ${template.metadata.complexity}`);
        }
        if (template.metadata.effectiveness) {
          console.log(`• Effectiveness: ${template.metadata.effectiveness}%`);
        }
      }
      
      // 显示变量
      console.log(colors.info('\nVariables:'));
      const varTable = new Table({
        columns: [
          { name: 'name', title: 'Name', alignment: 'left' },
          { name: 'type', title: 'Type', alignment: 'center' },
          { name: 'required', title: 'Required', alignment: 'center' },
          { name: 'default', title: 'Default', alignment: 'left' },
          { name: 'description', title: 'Description', alignment: 'left', maxLen: 30 }
        ]
      });
      
      for (const v of template.variables) {
        varTable.addRow({
          name: v.name,
          type: v.type,
          required: v.required ? '✓' : '-',
          default: v.default ? JSON.stringify(v.default) : '-',
          description: v.description || ''
        });
      }
      
      varTable.printTable();
      
      // 显示模板内容（前几行）
      console.log(colors.info('\nTemplate Preview:'));
      const lines = template.template.split('\n').slice(0, 10);
      lines.forEach(line => console.log(colors.dim(line)));
      if (template.template.split('\n').length > 10) {
        console.log(colors.dim('... (truncated)'));
      }
      
      // 显示示例
      if (options.example && template.examples && template.examples.length > 0) {
        console.log(colors.info('\nExample:'));
        const example = template.examples[0];
        console.log(colors.dim(JSON.stringify(example, null, 2)));
      }
    }));
  
  return command;
}

/**
 * prompt render - 渲染模板
 */
function createPromptRenderCommand(): Command {
  const command = new Command('render');
  
  command
    .description('Render a prompt template')
    .argument('<template>', 'Template ID')
    .option('-v, --vars <json>', 'Variables as JSON string')
    .option('-f, --file <path>', 'Variables from JSON file')
    .option('-o, --output <path>', 'Output file path')
    .option('-i, --interactive', 'Interactive mode')
    .option('--strict', 'Strict validation mode')
    .action(withErrorHandling(async (templateId: string, options) => {
      const engine = new PromptTemplateEngine();
      await engine.initialize();
      
      const template = engine.getTemplate(templateId);
      if (!template) {
        console.log(colors.error(`Template not found: ${templateId}`));
        return;
      }
      
      let variables: Record<string, any> = {};
      
      // 加载变量
      if (options.file) {
        const filePath = path.resolve(options.file);
        if (await fs.pathExists(filePath)) {
          variables = await fs.readJson(filePath);
        } else {
          throw new Error(`File not found: ${filePath}`);
        }
      } else if (options.vars) {
        try {
          variables = JSON.parse(options.vars);
        } catch (e) {
          throw new Error('Invalid JSON in --vars option');
        }
      } else if (options.interactive) {
        // 交互式收集变量
        variables = await collectVariablesInteractively(template);
      }
      
      // 渲染模板
      const output = engine.render(templateId, variables, {
        strict: options.strict
      });
      
      // 输出结果
      if (options.output) {
        const outputPath = path.resolve(options.output);
        await fs.ensureDir(path.dirname(outputPath));
        await fs.writeFile(outputPath, output, 'utf-8');
        console.log(colors.success(`✅ Output saved to: ${outputPath}`));
      } else {
        console.log(colors.header('\n📝 Rendered Output:\n'));
        console.log(output);
      }
    }));
  
  return command;
}

/**
 * prompt validate - 验证模板
 */
function createPromptValidateCommand(): Command {
  const command = new Command('validate');
  
  command
    .description('Validate prompt template syntax')
    .argument('<file>', 'Template file path')
    .action(withErrorHandling(async (filePath: string) => {
      const resolvedPath = path.resolve(filePath);
      
      if (!await fs.pathExists(resolvedPath)) {
        console.log(colors.error(`File not found: ${resolvedPath}`));
        return;
      }
      
      try {
        const content = await fs.readFile(resolvedPath, 'utf-8');
        const template = require('js-yaml').load(content);
        
        // 基本验证
        const errors: string[] = [];
        
        if (!template.id) errors.push('Missing required field: id');
        if (!template.name) errors.push('Missing required field: name');
        if (!template.template) errors.push('Missing required field: template');
        if (!template.variables || !Array.isArray(template.variables)) {
          errors.push('Missing or invalid field: variables (must be array)');
        }
        
        // 验证变量定义
        if (template.variables) {
          template.variables.forEach((v: any, i: number) => {
            if (!v.name) errors.push(`Variable ${i}: missing name`);
            if (!v.type) errors.push(`Variable ${i}: missing type`);
            if (v.type && !['string', 'number', 'boolean', 'array', 'object'].includes(v.type)) {
              errors.push(`Variable ${i}: invalid type "${v.type}"`);
            }
          });
        }
        
        // 验证 Handlebars 模板语法
        try {
          require('handlebars').compile(template.template);
        } catch (e: any) {
          errors.push(`Template syntax error: ${e.message}`);
        }
        
        if (errors.length > 0) {
          console.log(colors.error('❌ Validation failed:'));
          errors.forEach(e => console.log(colors.error(`  • ${e}`)));
        } else {
          console.log(colors.success('✅ Template is valid!'));
          
          // 显示模板信息
          console.log(colors.info('\nTemplate Info:'));
          console.log(`• ID: ${template.id}`);
          console.log(`• Name: ${template.name}`);
          console.log(`• Variables: ${template.variables.length}`);
          if (template.metadata) {
            console.log(`• Category: ${template.metadata.category || 'none'}`);
          }
        }
        
      } catch (error: any) {
        console.log(colors.error(`Failed to parse template: ${error.message}`));
      }
    }));
  
  return command;
}

/**
 * prompt export - 导出模板
 */
function createPromptExportCommand(): Command {
  const command = new Command('export');
  
  command
    .description('Export template to file')
    .argument('<template>', 'Template ID')
    .argument('<output>', 'Output file path')
    .option('--no-metadata', 'Exclude metadata')
    .action(withErrorHandling(async (templateId: string, outputPath: string, options) => {
      const engine = new PromptTemplateEngine();
      await engine.initialize();
      
      await engine.exportTemplate(
        templateId, 
        path.resolve(outputPath),
        options.metadata !== false
      );
      
      console.log(colors.success(`✅ Template exported to: ${outputPath}`));
    }));
  
  return command;
}

/**
 * 交互式收集变量
 */
async function collectVariablesInteractively(template: any): Promise<Record<string, any>> {
  const variables: Record<string, any> = {};
  
  console.log(colors.header('\n📝 Please provide variable values:\n'));
  
  for (const varDef of template.variables) {
    if (!varDef.required && varDef.default !== undefined) {
      // 有默认值的可选变量
      const answer = await inquirer.prompt([
        {
          type: 'confirm',
          name: 'useDefault',
          message: `Use default value for ${varDef.name}? (${JSON.stringify(varDef.default)})`,
          default: true
        }
      ]);
      
      if (answer.useDefault) {
        continue; // 使用默认值
      }
    }
    
    // 根据类型选择输入方式
    let promptConfig: any = {
      name: 'value',
      message: `${varDef.name}${varDef.description ? ' (' + varDef.description + ')' : ''}:`
    };
    
    if (varDef.enum) {
      promptConfig.type = 'list';
      promptConfig.choices = varDef.enum;
    } else if (varDef.type === 'boolean') {
      promptConfig.type = 'confirm';
      promptConfig.default = varDef.default || false;
    } else if (varDef.type === 'array') {
      promptConfig.type = 'input';
      promptConfig.message += ' (comma-separated)';
      promptConfig.filter = (input: string) => 
        input.split(',').map(s => s.trim()).filter(s => s);
    } else {
      promptConfig.type = 'input';
      if (varDef.default !== undefined) {
        promptConfig.default = varDef.default;
      }
    }
    
    if (varDef.required) {
      promptConfig.validate = (input: any) => {
        if (varDef.type === 'array') {
          return input.length > 0 || 'This field is required';
        }
        return (input !== '' && input !== undefined) || 'This field is required';
      };
    }
    
    const answer = await inquirer.prompt([promptConfig]);
    
    if (answer.value !== undefined && answer.value !== '') {
      variables[varDef.name] = answer.value;
    }
  }
  
  return variables;
}